<script lang="ts">
	import { mutation, query } from "@api/graphql.js";
	import sendApplication from "@api/mutations/sendApplication.graphql";
	import getDogByChipNumber from "@api/queries/getDogByChipNumber.graphql";
	import CartSummary from "@components/CartSummary.svelte";
	import ProductCard from "@components/ProductCard.svelte";
	import { applicationFormId } from "@components/applicationFormId.js";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import ErrorMessages from "@components/form/ErrorMessages.svelte";
	import FileUploader from "@components/form/FileUploader.svelte";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import { clearApplicationDraft, loadApplicationDraft, saveApplicationDraft } from "@utils/applicationDraft.js";
	import { getFormFields } from "@utils/getFormFields.js";
	import { getTranslate } from "@utils/translate.js";
	import { onMount, tick } from "svelte";
	import addIcon from "@core/assets/icons/add.svg?raw";
	import audioUpload from "@core/assets/icons/audio-upload.svg?raw";
	import removeIcon from "@core/assets/icons/cross.svg?raw";
	import type { Application, CreateApplicationInput } from "@core/schema/Application.js";
	import type { ApplicationValidationErrors } from "@core/schema/ApplicationCustomInvocation.js";
	import { Breed } from "@core/schema/Breed.js";
	import type { Competition } from "@core/schema/Competition.js";
	import { CompetitionCategoryType } from "@core/schema/CompetitionCategoryType.js";
	import { Country } from "@core/schema/Country.js";
	import type { Dog } from "@core/schema/Dog.js";
	import type { Gender } from "@core/schema/Gender.js";
	import type { Locale } from "@core/schema/Locale.js";
	import type { TShirtSize } from "@core/schema/TShirtSize.js";
	import { Team } from "@core/schema/Team.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { competitionCategoryTypeIncompatibilities } from "@core/schema/competitionCategoryTypeIncompatibilities.js";
	import { competitionCategoryTypesWithRequiredAudio } from "@core/schema/competitionCategoryTypesWithRequiredAudio.js";
	import { competitionCategoryTypesWithTshirt } from "@core/schema/competitionCategoryTypesWithTshirt.js";
	import { breedNameMap } from "@core/schema/names/breedNameMap.js";
	import { competitionCategoryTypeNameMap } from "@core/schema/names/competitionCategoryTypeNameMap.js";
	import { countryNameMap } from "@core/schema/names/countryNameMap.js";
	import { teamNameMap } from "@core/schema/names/teamNameMap.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getMaximumBirthdateOfDog } from "@core/schema/utils/getMaximumBirthdateOfDog.js";
	import { createApplicationCreateInputSchema } from "@core/schema/zod/applicationSchema.js";
	import type { DeepComplete } from "@core/utils/DeepComplete.js";
	import type { DeepPartial } from "@core/utils/DeepPartial.js";
	import { AppSyncError } from "@core/utils/aws/appsync/AppSyncError.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { formatEur } from "@core/utils/formatEur.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { isZodError } from "@core/utils/isZodError.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./ApplicationForm.translations.json" with { type: "json" };
	import PaymentQrCodes from "./PaymentQrCodes.svelte";
	import Icon from "./common/Icon.svelte";

	const { competition }: { competition: Competition } = $props();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const applicationInputSchema = createApplicationCreateInputSchema(tenant.locale);
	const translate = getTranslate(translations);

	const send = createPromiseWithStatus((input: CreateApplicationInput) =>
		mutation<Application, { input: CreateApplicationInput; locale: Locale }>(PUBLIC_GRAPHQL_ENDPOINT, sendApplication, {
			input,
			locale: tenant.locale,
		}),
	);
	const getDog = createPromiseWithStatus((chipNumber: Dog["chipNumber"]) =>
		query<Dog | undefined, { chipNumber: Dog["chipNumber"] }>(PUBLIC_GRAPHQL_ENDPOINT, getDogByChipNumber, {
			chipNumber,
		}),
	);

	type Input = Omit<DeepComplete<DeepPartial<CreateApplicationInput>>, "audio"> & {
		audio: CreateApplicationInput["audio"];
	};

	let dogs = $state<{ id: ReturnType<typeof crypto.randomUUID>; done: boolean }[]>([
		{ id: crypto.randomUUID(), done: false },
	]);

	let uploadedAudio = $state<Record<(typeof dogs)[number]["id"], UploadedFile | undefined>>({});
	let formInputs = $state<[(typeof dogs)[number]["id"], Input][]>([]);
	let errors = $state<[(typeof dogs)[number]["id"], { error: unknown; input?: CreateApplicationInput }][]>([]);
	let successfulApplications = $state<[CreateApplicationInput, Application][]>([]);
	let dogBirthdateErrors = $state<Record<string, string>>({});

	function addDog(): void {
		dogs.push({ id: crypto.randomUUID(), done: false });
	}
	function removeDog(toRemove: string): void {
		dogs = dogs.filter(({ id }) => id !== toRemove);
	}

	let recordingApprovedAt = $state<string | undefined>(undefined);
	let gdprApprovedAt = $state<string | undefined>(undefined);
	function onRecordingToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		recordingApprovedAt = input.checked ? new Date().toISOString() : undefined;
	}
	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : undefined;
	}

	function getFormInputs(fields: Record<string, string | undefined>): typeof formInputs {
		return dogs.map(({ id }) => {
			const competitionCategoryTypes: CompetitionCategoryType[] = [];
			for (const type of Object.values(CompetitionCategoryType)) {
				const group = competitionCategoryTypeIncompatibilities.find((group) => group.includes(type));
				if (group) {
					const key = group.join(":");
					const value = fields[`${id}-competitionCategoryTypes:${key}`];
					if (value === type) {
						competitionCategoryTypes.push(type);
					}
				} else if (fields[`${id}-competitionCategoryTypes:${type}`] === "on") {
					competitionCategoryTypes.push(type);
				}
			}
			return [
				id,
				{
					audio: competitionCategoryTypesWithRequiredAudio.some((type) => competitionCategoryTypes.includes(type))
						? uploadedAudio[id]
						: undefined,
					competitionCategoryTypes,
					competitionId: competition.id,
					dog: {
						birthdate: fields[`${id}-birthdate`],
						breed: fields[`${id}-customBreed`] ?? fields[`${id}-breed`],
						callName: fields[`${id}-callName`],
						chipNumber: fields[`${id}-chipNumber`],
						registeredName: fields[`${id}-registeredName`],
						handlerOwner: undefined,
					},
					gdprApprovedAt,
					handler: {
						email: fields.email,
						firstName: fields.firstName,
						homeland: fields.homeland as Country | undefined,
						lastName: fields.lastName,
						member: fields.member === "on",
					},
					isHandlerOwnerOfDog: fields[`${id}-isHandlerOwnerOfDog`] === "on",
					recordingApprovedAt,
					tShirt:
						fields.tShirtGender !== undefined &&
						fields.tShirtSize !== undefined &&
						competitionCategoryTypes.some((type) => competitionCategoryTypesWithTshirt.includes(type))
							? {
									size: fields.tShirtSize as TShirtSize,
									gender: fields.tShirtGender as Gender,
								}
							: undefined,
					team: fields.team as Team | undefined,
				},
			];
		});
	}

	function onchange(event: Event): void {
		const input = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const fields = getFormFields(input.form!);
		formInputs = getFormInputs(fields);
		errors = []; // TODO (not that important): Only clear fields which changed.
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		persistDraft();
	}

	const loadedDogs: Record<Dog["chipNumber"], Dog | null> = $state({});
	$effect(() => {
		if (getDog.value.status === "loading") {
			return;
		}

		for (const [_, input] of formInputs) {
			const chipNumber = input.dog?.chipNumber;
			if (!chipNumber || chipNumber in loadedDogs) {
				continue;
			}
			void getDog.call(chipNumber).then((dog) => {
				loadedDogs[chipNumber] = dog ?? null;
				input.dog = {
					chipNumber,
					breed: dog?.breed,
					callName: dog?.callName,
					registeredName: dog?.registeredName,
					birthdate: dog?.birthdate,
					handlerOwner: undefined,
				};
			});
			break;
		}
	});

	// ── Draft persistence ─────────────────────────────────────────────────────
	// Keep a half-filled form in localStorage so entered values survive e.g. a
	// language switch (which is a different URL). See utils/applicationDraft.ts.
	let restoring = false;
	let restorePending: Record<string, string> | null = null;

	function draftForm(): HTMLFormElement | null {
		return typeof document === "undefined"
			? null
			: (document.getElementById(applicationFormId[tenant.locale]) as HTMLFormElement | null);
	}

	function persistDraft(): void {
		if (restoring) {
			return;
		}
		const form = draftForm();
		if (form) {
			saveApplicationDraft(
				competition.id,
				getFormFields(form),
				dogs.map((dog) => dog.id),
			);
		}
	}

	function applyRestore(): void {
		const form = draftForm();
		if (!form || !restorePending) {
			return;
		}
		const remaining = restorePending;
		for (const element of Array.from(form.elements)) {
			if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) {
				continue;
			}
			const { name } = element;
			if (!name || !(name in remaining)) {
				continue;
			}
			// DB-prefilled / locked fields own their value — don't overwrite them.
			if (element.disabled || (element instanceof HTMLInputElement && element.readOnly)) {
				// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
				delete remaining[name];
				continue;
			}
			const value = remaining[name];
			if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
				if (element.type === "radio") {
					if ((element.value || "on") !== value) {
						continue; // wait for the radio in this group whose value matches
					}
					element.checked = true;
				} else {
					element.checked = (element.value || "on") === value;
				}
			} else {
				element.value = value ?? "";
			}
			element.dispatchEvent(new Event("input", { bubbles: true }));
			element.dispatchEvent(new Event("change", { bubbles: true }));
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete remaining[name];
		}
		if (Object.keys(remaining).length === 0) {
			restorePending = null;
			restoring = false;
		}
	}

	onMount(() => {
		const draft = loadApplicationDraft(competition.id);
		if (!draft) {
			return;
		}
		restoring = true;
		if (draft.dogs.length > 0) {
			dogs = draft.dogs.map((id) => ({ id: id as ReturnType<typeof crypto.randomUUID>, done: false }));
		}
		restorePending = { ...draft.fields };
		void tick().then(applyRestore);
		// Fallback: never stay in "restoring" mode forever if a field never renders.
		const stop = setTimeout(() => {
			restoring = false;
			restorePending = null;
		}, 6000);
		return (): void => {
			clearTimeout(stop);
		};
	});

	// Re-apply remaining draft values as more fields appear (dogs added, chip
	// lookups resolve and reveal the dog-detail fields).
	$effect(() => {
		void formInputs;
		void loadedDogs;
		if (restorePending) {
			void tick().then(applyRestore);
		}
	});

	async function onsubmit(event: SubmitEvent): Promise<void> {
		errors = [];
		dogBirthdateErrors = {};
		successfulApplications = [];
		event.preventDefault();
		const form = event.target as HTMLFormElement;
		const fields = getFormFields(form);
		const newErrors: typeof errors = [];

		for (const [dogId, formInput] of getFormInputs(fields)) {
			const dog = dogs.find(({ id }) => id === dogId);
			if (dog?.done) {
				continue;
			}

			const parsedInput = applicationInputSchema.safeParse(formInput);
			if (!parsedInput.success) {
				// eslint-disable-next-line no-console
				console.error(parsedInput.error);
				newErrors.push([dogId, { error: parsedInput.error }]);
				continue;
			}

			const { data: input } = parsedInput;
			try {
				const application = await send.call(input);
				successfulApplications.push([input, application]);
				dogs = dogs.map((dog) => (dog.id === dogId ? { ...dog, done: true } : dog));
				if (loadedDogs[input.dog.chipNumber] === null) {
					// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
					delete loadedDogs[input.dog.chipNumber];
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				newErrors.push([dogId, { error, input }]);
			}
		}

		formInputs = getFormInputs(getFormFields(form));

		errors = newErrors;

		// Everything went through → the saved draft is no longer needed.
		if (newErrors.length === 0) {
			clearApplicationDraft();
		}
	}

	function getValidationErrorMessages(input: CreateApplicationInput, error: unknown): string[] {
		if (!(error instanceof AppSyncError)) {
			return [translate("internalError")];
		}
		const [firstError] = error.errors;
		if (!firstError?.errorType) {
			return [translate("internalError")];
		}

		switch (firstError.errorType) {
			case "Application:Invalid": {
				const errors: string[] = [];
				const validationError = firstError.errorInfo as ApplicationValidationErrors;
				// eslint-disable-next-line no-console
				console.error(validationError);

				for (const category of validationError.missingAudioForCategories ?? []) {
					errors.push(translate("missingAudio", { category: competitionCategoryTypeNameMap[category][tenant.locale] }));
				}
				for (const category of validationError.handlerDogAlreadyHaveApplicationInCategories ?? []) {
					errors.push(
						translate("alreadyExistingApplication", {
							category: competitionCategoryTypeNameMap[category][tenant.locale],
							dog: input.dog.callName,
						}),
					);
				}
				for (const categories of validationError.handlerOnlyAllowedOnceInCategories ?? []) {
					const [category, ...otherCategories] = categories;
					if (category !== undefined) {
						if (otherCategories.length > 0) {
							errors.push(
								translate("alreadyOnceInCategoryGroup", {
									categories: categories
										.map((category) => competitionCategoryTypeNameMap[category][tenant.locale])
										.join("/"),
								}),
							);
						} else {
							errors.push(
								translate("alreadyOnceInCategory", {
									category: competitionCategoryTypeNameMap[category][tenant.locale],
								}),
							);
						}
					}
				}
				for (const category of validationError.competitionDoesNotHaveCategories ?? []) {
					errors.push(
						translate("competitionNonexistentCategory", {
							category: competitionCategoryTypeNameMap[category][tenant.locale],
						}),
					);
				}
				for (const categories of validationError.categoryIncompatibilities ?? []) {
					errors.push(
						translate("categoryIncompatibilities", {
							categories: categories
								.map((category) => competitionCategoryTypeNameMap[category][tenant.locale])
								.join(", "),
						}),
					);
				}
				if (validationError.dogMaximumBirthdate) {
					errors.push(
						translate("maximumDogBirthdate", {
							date: toDateMonthYearFormat(validationError.dogMaximumBirthdate, tenant.locale),
						}),
					);
				}
				return errors;
			}
			default:
				return [translate("internalError")];
		}
	}

	function onBirthdateInput(event: Event, max: Date): void {
		const input = event.currentTarget as HTMLInputElement;
		const { dogId } = input.dataset;
		if (!dogId) {
			return;
		}
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete dogBirthdateErrors[dogId];
		const date = new Date(input.value);
		if (date > max) {
			dogBirthdateErrors[dogId] = translate("maximumDogBirthdate", { date: toDateMonthYearFormat(max, tenant.locale) });
		}
	}

	const isBreed = isEnum(Breed);

	// ── Pricing / cart summary ────────────────────────────────────────────────
	// Prices come straight off the competition's categories (CZK); the sticky
	// summary and the per-discipline chip labels both read from here.
	const eurRate = $derived(competition.payment?.eurRate);
	function priceOfCategory(type: CompetitionCategoryType): number {
		return competition.categories.find((category) => category.type === type)?.price ?? 0;
	}
	const cartBreakdown = $derived(
		dogs.map((dog, index) => {
			const input = formInputs.find(([id]) => id === dog.id)?.[1];
			const items = (input?.competitionCategoryTypes ?? [])
				.filter((type): type is CompetitionCategoryType => type !== undefined)
				.map((type) => ({ type, price: priceOfCategory(type) }));
			return {
				id: dog.id,
				index,
				callName: input?.dog?.callName,
				items,
				subtotal: items.reduce((sum, item) => sum + item.price, 0),
			};
		}),
	);
	const cartTotalCzk = $derived(cartBreakdown.reduce((sum, dog) => sum + dog.subtotal, 0));

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet failedApplication([_, { error, input }]: (typeof errors)[number])}
	<div class="flex flex-col gap-2 mt-4 text-primary">
		{#if input}
			{@const messages = getValidationErrorMessages(input, error)}
			<p class="font-bold">{translate("applicationProblems")}</p>
			<ErrorMessages {messages} />
		{:else}
			<ErrorMessages messages={[translate("wronglyFilled")]} />
		{/if}
	</div>
{/snippet}

<Modal show={successfulApplications.length > 0} maxWidth="max-w-4xl">
	<div class="flex flex-col gap-6">
		<!-- Success header -->
		<div class="flex flex-col items-center gap-3 text-center">
			<span class="bg-grass-100 text-grass-600 flex h-14 w-14 items-center justify-center rounded-full">
				<svg
					class="h-7 w-7"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<path d="M20 6 9 17l-5-5" />
				</svg>
			</span>
			<h3 class="!mb-0 !text-[1.6rem]">{translate("applicationSentTitle")}</h3>
			<p class="text-ink-600 text-sm">{translate("thanksForApplication")}</p>
		</div>

		<PaymentQrCodes
			{competition}
			applications={successfulApplications.map(([input, app]) => ({
				application: app,
				label: `${input.dog.callName} · ${input.dog.chipNumber}`,
			}))}
		/>

		<!-- Submitted dogs summary -->
		<div class="border-ink-200 rounded-xl border bg-ink-50 p-4">
			<p class="eyebrow text-ink-500 mb-3">{translate("successfulApplications")}</p>
			<ul class="divide-ink-200 flex flex-col divide-y">
				{#each successfulApplications as [input, application] (application.id)}
					<li class="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
						<span class="text-ink-900 font-semibold">{input.dog.callName}</span>
						<span class="text-ink-600 text-sm">
							<span class="tabular">{input.dog.chipNumber}</span>
							· {application.categories
								.map(({ type }) => competitionCategoryTypeNameMap[type][tenant.locale])
								.join(", ")}
						</span>
					</li>
				{/each}
			</ul>
		</div>

		{#if errors.length > 0}
			<div class="border-primary-200 bg-primary-50 flex flex-col gap-2 rounded-xl border p-4">
				<p class="text-primary font-bold">{translate("errorApplications")}</p>
				{#each errors as error (error[0])}
					{@render failedApplication(error)}
				{/each}
			</div>
		{/if}

		<Button
			variant={ButtonVariant.Primary}
			class="w-full sm:ml-auto sm:w-auto"
			onclick={(): void => {
				successfulApplications = [];
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<Form class="mb-16 w-full" id={applicationFormId[tenant.locale]} oninput={persistDraft} {onchange} {onsubmit}>
	<!-- Title spans the full width; the two-column grid (form | summary) sits below,
	     so the summary aligns with the first form card rather than the eyebrow. -->
	<div class="ff-fade-up mb-6 sm:mb-8">
		<p class="eyebrow text-secondary mb-2">{translate("applicationEyebrow")}</p>
		<h2 class="!mb-0">{translate("application")}</h2>
	</div>

	<div class="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-8">
		<div class="flex min-w-0 flex-col gap-5 pb-28 sm:gap-6 lg:pb-0">
			<section class="border-ink-200 shadow-card rounded-xl border bg-white p-5 sm:p-7">
				<div class="border-ink-100 mb-6 flex items-center gap-3 border-b pb-4">
					<span
						class="bg-secondary-50 text-secondary font-mono flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold"
					>
						01
					</span>
					<h3 class="!mb-0 !text-[1.4rem]">{translate("handler")}</h3>
				</div>
				<div class="grid gap-4 sm:grid-cols-2 sm:gap-5">
					<LabelledInput type="text" name="firstName" placeholder="John" required>
						{translate("handlerFirstname")}
					</LabelledInput>
					<LabelledInput type="text" name="lastName" placeholder="Doe" required>
						{translate("handlerLastname")}
					</LabelledInput>
					<div class="sm:col-span-2">
						<LabelledInput type="email" name="email" placeholder="johndoe@gmail.com" required>
							{translate("email")}
						</LabelledInput>
					</div>
					<LabelledSelect name="homeland" required>
						{translate("homeland")}
						{#snippet options()}
							<option value="">{translate("choose")}</option>
							{#each Object.values(Country) as country (country)}
								<option value={country}>{countryNameMap[country][tenant.locale]}</option>
							{/each}
						{/snippet}
					</LabelledSelect>
					<LabelledSelect name="team">
						{translate("team")}
						{#snippet options()}
							<option value="">{translate("none")}</option>
							{#each Object.values(Team) as team (team)}
								<option value={team}>{teamNameMap[team][tenant.locale]}</option>
							{/each}
						{/snippet}
					</LabelledSelect>

					<div class="sm:col-span-2">
						<LabelledInput type="checkbox" name="member">
							{translate("declarationOfMembershipDiscDogClub")}
						</LabelledInput>
					</div>
				</div>
			</section>

			<div class="flex flex-col gap-5 sm:gap-6">
				{#each dogs as dog, index (dog.id)}
					{@const [, formInput] = formInputs.find(([id]) => id === dog.id) ?? []}
					{@const error = errors.find(([id]) => id === dog.id)}
					{@const [, output] = error ?? []}
					{@const loadedDog = loadedDogs[formInput?.dog?.chipNumber ?? ""]}
					{@const loadingDog =
						getDog.value.status === "loading" && getDog.value.variables[0] === (formInput?.dog?.chipNumber ?? "")}
					{@const selectedCategoriesWhichRequireAudio = competitionCategoryTypesWithRequiredAudio.filter((type) =>
						formInput?.competitionCategoryTypes?.includes(type),
					)}
					{@const maxBirthDate = getMaximumBirthdateOfDog(
						competition,
						formInput?.competitionCategoryTypes?.filter((type) => type !== undefined) ?? [],
					)}
					{@const birthdateErrors = dogBirthdateErrors[dog.id]}
					<section
						class="shadow-card relative rounded-xl border bg-white p-5 sm:p-7 {dog.done
							? 'border-grass-500'
							: 'border-ink-200'}"
					>
						<div class="border-ink-100 mb-6 flex items-center justify-between gap-3 border-b pb-4">
							<div class="flex items-center gap-3">
								<span
									class="font-mono flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold {dog.done
										? 'bg-grass-100 text-grass-600'
										: 'bg-secondary-50 text-secondary'}"
								>
									{(index + 1).toString().padStart(2, "0")}
								</span>
								<h3 class="!mb-0 !text-[1.4rem]" class:text-grass-600={dog.done}>
									{translate("dog")} #{index + 1}{#if dog.done}
										· {translate("registered")}{/if}
								</h3>
							</div>
							{#if index > 0 && !dog.done}
								<Button
									class="min-w-fit h-fit flex items-center gap-2"
									variant={ButtonVariant.SecondaryOutline}
									onclick={(): void => {
										removeDog(dog.id);
									}}
								>
									{translate("removeDog")}
									<Icon class="h-3 aspect-square" icon={removeIcon} />
								</Button>
							{/if}
						</div>

						<div class="grid gap-6 md:grid-cols-2 md:gap-8">
							<div class="flex flex-col gap-4">
								<p class="eyebrow text-ink-500">{translate("dogIdentification")}</p>
								<LabelledInput
									type="text"
									name="{dog.id}-chipNumber"
									placeholder="933007125056789"
									required
									readonly={dog.done}
								>
									{translate("dogChipOrTatoo")}
								</LabelledInput>
								{#if !dog.done && loadedDog === undefined && !loadingDog}
									<p class="text-ink-500 -mt-1 flex items-start gap-1.5 text-xs leading-snug">
										<svg
											class="text-secondary mt-0.5 h-3.5 w-3.5 shrink-0"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										>
											<circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
										</svg>
										{translate("chipHint")}
									</p>
								{/if}
								{#if loadingDog}
									<p>{translate("searchingDog")}</p>
								{:else if loadedDog !== undefined}
									<LabelledInput
										type="text"
										name="{dog.id}-registeredName"
										placeholder="Pretty II. Tiann"
										required
										value={loadedDog?.registeredName}
										readonly={loadedDog !== null || dog.done}
									>
										{translate("dogRegisteredName")}
									</LabelledInput>
									<LabelledInput
										type="text"
										name="{dog.id}-callName"
										placeholder="Leia"
										required
										value={loadedDog?.callName}
										readonly={loadedDog !== null || dog.done}
										oninput={onchange}
									>
										{translate("dogCallName")}
									</LabelledInput>
									<div>
										<LabelledInput
											type="date"
											data-dog-id={dog.id}
											name="{dog.id}-birthdate"
											placeholder="05/08/2022"
											oninput={(event): void => {
												// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
												onBirthdateInput(event, maxBirthDate.toJSDate());
											}}
											max={maxBirthDate.toISODate()}
											required
											value={loadedDog?.birthdate}
											readonly={loadedDog !== null || dog.done}
										>
											{translate("dogBirthdate")}
										</LabelledInput>
										{#if birthdateErrors}
											<ErrorMessages class="mt-4" messages={[birthdateErrors]} />
										{/if}
									</div>
									{#if !loadedDog?.handlerOwner}
										<LabelledInput type="checkbox" name="{dog.id}-isHandlerOwnerOfDog" readonly={dog.done}>
											{translate("isDogOwner")}
										</LabelledInput>
									{/if}
									<LabelledSelect name="{dog.id}-breed" required>
										{translate("dogBreed")}
										{#snippet options()}
											<option value="" disabled={loadedDog !== null || dog.done}>{translate("choose")}</option>
											{#each Object.entries(breedNameMap) as [value, name] (value)}
												{@const selected = formInput?.dog?.breed === value || value === loadedDog?.breed}
												<option {value} {selected} disabled={!selected && (loadedDog !== null || dog.done)}>
													{name[tenant.locale]}
												</option>
											{/each}
											<option
												value="other"
												disabled={loadedDog !== null || dog.done}
												selected={loadedDog && !isBreed(loadedDog.breed)}
											>
												{translate("customBreed")}
											</option>
										{/snippet}
									</LabelledSelect>
									{#if (loadedDog ?? formInput?.dog)?.breed && !isBreed((loadedDog ?? formInput?.dog)?.breed)}
										<LabelledInput
											type="text"
											name="{dog.id}-customBreed"
											required
											value={loadedDog?.breed}
											readonly={loadedDog !== null || dog.done}
										>
											{translate("customDogBreed")}
										</LabelledInput>
									{/if}
								{/if}
							</div>
							<div>
								<p class="eyebrow text-ink-500 mb-3">{translate("disciplines")}</p>
								<div class="flex flex-wrap gap-2">
									{#each Object.values(CompetitionCategoryType).filter( (categoryType) => competition.categories.some(({ type }) => type === categoryType), ) as category (category)}
										{@const incompatibilityGroup = competitionCategoryTypeIncompatibilities.find((group) =>
											group.includes(category),
										)}
										{@const disabled = dog.done}
										{@const name = `${dog.id}-competitionCategoryTypes:${incompatibilityGroup?.join(":") ?? category}`}
										{@const price = priceOfCategory(category)}
										{@const priceEur = formatEur(price, eurRate, tenant.locale)}
										<label class="cursor-pointer">
											<input
												class="peer sr-only"
												type={incompatibilityGroup ? "radio" : "checkbox"}
												{name}
												value={incompatibilityGroup ? category : undefined}
												{disabled}
												onclick={(event) => {
													// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
													const input = event.target as HTMLInputElement;
													if (input.type === "radio" && input.checked) {
														if (input.dataset.wasChecked === "true") {
															input.checked = false;
															input.dispatchEvent(new Event("input", { bubbles: true }));
															input.dispatchEvent(new Event("change", { bubbles: true }));
															delete input.dataset.wasChecked;
														} else {
															for (const input of document.querySelectorAll<HTMLInputElement>(
																`input[name="${name}"]`,
															)) {
																delete input.dataset.wasChecked;
															}
															input.dataset.wasChecked = "true";
														}
													}
												}}
											/>
											<span
												class="peer-checked:border-secondary peer-checked:bg-secondary peer-checked:text-white peer-hover:border-ink-400 peer-focus-visible:ring-secondary/30 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 border-ink-300 text-ink-700 inline-flex items-center gap-1.5 rounded-full border-2 bg-white px-4 py-2 text-sm font-semibold transition-colors duration-150 peer-focus-visible:ring-4"
											>
												<span>{competitionCategoryTypeNameMap[category][tenant.locale]}</span>
												<span class="tabular font-normal opacity-70">· {formatPrice(price, tenant.locale, false)}</span>
												{#if priceEur}
													<span class="tabular text-xs font-normal opacity-60">≈ {priceEur}</span>
												{/if}
											</span>
										</label>
									{/each}
								</div>
								{#if selectedCategoriesWhichRequireAudio.length > 0}
									<div class="mt-4 lg:mt-6 max-w-96">
										<!-- Required in FileUploader does not work correctly, we have schema validation anyways, so I can't care much anymore. -->
										<FileUploader
											destinationFolder="audio/{dog.id}"
											accept={["audio/mp3", "audio/mpeg", "audio/wav"]}
											uploadIcon={audioUpload}
											onchange={(file: UploadedFile | undefined): void => {
												uploadedAudio[dog.id] = file;
												uploadedAudio = uploadedAudio;
											}}
											disabled={dog.done}
										/>
									</div>
								{/if}

								{#if isZodError<CreateApplicationInput>(output?.error) && output.error.formErrors.fieldErrors.competitionCategoryTypes}
									<ErrorMessages class="mt-4" messages={output.error.formErrors.fieldErrors.competitionCategoryTypes} />
								{/if}
							</div>
						</div>
					</section>
					{#if error}
						{@render failedApplication(error)}
					{/if}
				{/each}
			</div>

			<div>
				<Button
					class="flex w-full items-center justify-center gap-2 sm:w-auto"
					variant={ButtonVariant.SecondaryOutline}
					onclick={addDog}
				>
					{translate("addDog")}
					<Icon class="h-5 aspect-square" icon={addIcon} />
				</Button>
			</div>

			{#if competition.tShirtEnabled && competition.tShirtPhoto && formInputs.some( ([, input]) => input.competitionCategoryTypes?.some((type) => type !== undefined && competitionCategoryTypesWithTshirt.includes(type)), )}
				<section class="border-ink-200 shadow-card rounded-xl border bg-white p-5 sm:p-7">
					<div class="border-ink-100 mb-6 flex items-center gap-3 border-b pb-4">
						<span
							class="bg-secondary-50 text-secondary font-mono flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold"
						>
							+
						</span>
						<h3 class="!mb-0 !text-[1.4rem]">{translate("selectTshirtSize")}</h3>
					</div>
					<div class="flex flex-wrap justify-center md:justify-start">
						<ProductCard photo={competition.tShirtPhoto} />
					</div>
				</section>
			{/if}

			<section class="border-ink-200 shadow-card rounded-xl border bg-white p-5 sm:p-7">
				<div class="flex flex-col gap-5">
					<LabelledInput type="checkbox" onchange={onRecordingToggle} required>
						<Formatted
							text={translate("consentPhotos", { path: `${getGdprPath(tenant.locale)}#multimedialni-obsah` })}
						/>
					</LabelledInput>
					<LabelledInput type="checkbox" onchange={onGdprToggle} required>
						<Formatted text={translate("consentGdpr", { path: `${getGdprPath(tenant.locale)}#poskytnuti-udaju` })} />
					</LabelledInput>
				</div>
			</section>
		</div>

		<aside class="hidden lg:block">
			<div class="lg:sticky lg:top-24">
				<CartSummary
					breakdown={cartBreakdown}
					totalCzk={cartTotalCzk}
					{eurRate}
					locale={tenant.locale}
					sending={send.value.status === "loading"}
					submitLabel={translate("sendApplication")}
					sendingLabel={translate("sendingApplication")}
				/>
			</div>
		</aside>
	</div>

	<CartSummary
		compact
		breakdown={cartBreakdown}
		totalCzk={cartTotalCzk}
		{eurRate}
		locale={tenant.locale}
		sending={send.value.status === "loading"}
		submitLabel={translate("sendApplication")}
		sendingLabel={translate("sendingApplication")}
	/>
</Form>
